import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CampSiteCard from './CampSiteCard';
import { useToast } from "@/hooks/use-toast";
import { listCampingSites } from '@/services/campingSite';
import { CampingSite, SiteAmenities, FrontendAmenities } from '@/services/campingSite/types';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from "@/components/ui/button";
import { CalendarRange, Search, X, RefreshCcw } from 'lucide-react';
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useSiteAvailability } from '@/contexts/SiteAvailabilityContext';

interface CampSiteGridProps {
  siteType: 'rv' | 'wild-horse';
  locationFilter?: string;
  startDate?: Date;
  endDate?: Date;
}

const CampSiteGrid: React.FC<CampSiteGridProps> = ({ 
  siteType,
  locationFilter = 'all',
  startDate: initialStartDate,
  endDate: initialEndDate
}) => {
  const navigate = useNavigate(); // Add useNavigate hook
  const { toast } = useToast();
  const { refreshedSiteIds, refreshAvailability } = useSiteAvailability();
  const [sites, setSites] = useState<CampingSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [searchStartDate, setSearchStartDate] = useState<Date | undefined>(initialStartDate);
  const [searchEndDate, setSearchEndDate] = useState<Date | undefined>(initialEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | undefined>(initialStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | undefined>(initialEndDate);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Function to fetch sites with availability information
  const fetchSites = async (forceRefresh = false) => {
    if (isRefreshing && !forceRefresh) return;
    
    setIsLoading(true);
    setIsRefreshing(true);
    
    try {
      let dbSiteType = siteType;
      let dbLocation = locationFilter !== 'all' ? locationFilter as any : undefined;
      
      let wildHorseFilter: string | undefined = undefined;
      if (siteType === 'wild-horse' && locationFilter !== 'all') {
        wildHorseFilter = locationFilter;
        dbLocation = 'wild-horse';
      }
      
      console.log('Fetching camping sites with params:', {
        siteType: dbSiteType, 
        location: dbLocation,
        wildHorseFilter,
        startDate: appliedStartDate,
        endDate: appliedEndDate
      });
      
      // Add date filtering if dates are selected
      const { sites: campingSites } = await listCampingSites({
        siteType: dbSiteType,
        location: dbLocation,
        active: true,
        startDate: appliedStartDate,
        endDate: appliedEndDate
      });
      
      let filteredSites = [...campingSites];
      if (siteType === 'wild-horse' && wildHorseFilter) {
        filteredSites = filteredSites.filter(site => {
          const siteNumber = parseInt(site.siteNumber.replace('WH-', '').trim(), 10);
          
          if (wildHorseFilter === 'north') {
            return siteNumber >= 1 && siteNumber <= 10;
          } else if (wildHorseFilter === 'south') {
            return siteNumber >= 11 && siteNumber <= 20;
          } else if (wildHorseFilter === 'east') {
            return siteNumber >= 21 && siteNumber <= 26;
          }
          
          return true;
        });
      }
      
      // If we're filtering for availability, only show available sites
      if (showOnlyAvailable && appliedStartDate && appliedEndDate) {
        filteredSites = filteredSites.filter(site => site.available !== false);
      }
      
      const sortedSites = [...filteredSites].sort((a, b) => {
        const aMatch = a.siteNumber.match(/\d+/);
        const bMatch = b.siteNumber.match(/\d+/);
        
        if (aMatch && bMatch) {
          const aNum = parseInt(aMatch[0], 10);
          const bNum = parseInt(bMatch[0], 10);
          return aNum - bNum;
        }
        
        return a.siteNumber.localeCompare(b.siteNumber);
      });
      
      // Log information about all sites
      console.log(`Fetched ${sortedSites.length} camping sites with ${sortedSites.filter(site => site.available === false).length} unavailable`);
      
      // Enhanced logging for SC-RV-02 specifically
      const scRv02 = sortedSites.find(site => site.siteNumber === 'SC-RV-02');
      if (scRv02) {
        console.log('SC-RV-02 availability status:', {
          available: scRv02.available,
          hasBookedDates: scRv02.bookedDates && scRv02.bookedDates.length > 0,
          bookedDatesCount: scRv02.bookedDates?.length,
          bookedDates: scRv02.bookedDates
        });
      }
      
      // Enhanced logging for MV-RV-01 specifically
      const mvRv01 = sortedSites.find(site => site.siteNumber === 'MV-RV-01');
      if (mvRv01) {
        console.log('MV-RV-01 availability status:', {
          available: mvRv01.available,
          hasBookedDates: mvRv01.bookedDates && mvRv01.bookedDates.length > 0,
          bookedDatesCount: mvRv01.bookedDates?.length,
          bookedDates: mvRv01.bookedDates
        });
      }
      
      const processedSites = sortedSites.map(site => {
        // Check if this site has been refreshed recently via our context
        if (refreshedSiteIds.includes(site.id)) {
          console.log(`Site ${site.id} was recently refreshed - checking current availability`);
          // If we have dates selected, check real-time availability
          if (appliedStartDate && appliedEndDate) {
            checkSiteAvailability(site.id, appliedStartDate, appliedEndDate)
              .then(isAvailable => {
                if (site.available !== isAvailable) {
                  console.log(`Updating site ${site.id} availability to ${isAvailable}`);
                  setSites(prevSites => {
                    return prevSites.map(s => {
                      if (s.id === site.id) {
                        return { ...s, available: isAvailable };
                      }
                      return s;
                    });
                  });
                }
              });
          }
        }
        
        if (site.location === 'sheep-creek' && site.siteNumber.toLowerCase().includes('rv') && site.hasElectricity) {
          console.log(`Ensuring Sheep Creek RV site ${site.siteNumber} has electric50Amp set to true`);
          return {
            ...site,
            amenities: {
              ...site.amenities,
              electric50Amp: true,
              amperage: 30
            }
          };
        }
        return site;
      });
      
      setSites(processedSites);
    } catch (err) {
      console.error('Error fetching camping sites:', err);
      setError('Failed to load camping sites. Please try again later.');
      toast({
        title: "Error",
        description: "Failed to load camping sites. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchSites();
  }, [siteType, locationFilter, toast, appliedStartDate, appliedEndDate, showOnlyAvailable, refreshedSiteIds]);
  
  // Enhanced real-time subscription for site booking updates
  useEffect(() => {
    if (sites.length === 0) return;
    
    console.log('Setting up real-time subscription for site booking changes');
    
    // Create subscriptions for both site_bookings and camping_sites tables
    const bookingsChannel = supabase.channel('site-booking-changes')
      .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'site_bookings' }, 
          (payload) => {
        console.log('Site booking changed:', payload);
        
        // Safely access the payload properties
        const newData = payload.new as Record<string, any> | null;
        const eventType = payload.eventType;
        
        // Take different actions based on the event type
        if (newData && newData.site_id) {
          switch(eventType) {
            case 'INSERT': // New booking created
              handleBookingCreated(newData);
              break;
            case 'UPDATE': // Booking updated (payment status change, etc)
              handleBookingUpdated(newData);
              break;
            case 'DELETE': // Booking canceled/deleted
              handleBookingDeleted(newData);
              break;
            default:
              console.log(`Unknown event type: ${eventType}`);
          }
        }
      })
      .subscribe((status) => {
        console.log(`Booking subscription status: ${status}`);
      });
      
    // NEW: Also listen for changes to the camping_sites table for booked_dates updates
    const sitesChannel = supabase.channel('camping-sites-changes')
      .on('postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'camping_sites' },
          (payload) => {
        console.log('Camping site updated:', payload);
        
        // If booked_dates was updated, check if it affects our current view
        const newData = payload.new as Record<string, any> | null;
        const oldData = payload.old as Record<string, any> | null;
        const siteId = newData?.id;
        
        // If status changed (admin flipped active/coming_soon/maintenance/etc),
        // re-fetch the entire list so newly hidden sites disappear and Coming Soon
        // sites get the badge applied without a manual refresh.
        if (newData && oldData && siteId) {
          const statusChanged = newData.status !== oldData.status;
          const statusMessageChanged = newData.status_message !== oldData.status_message;
          if (statusChanged || statusMessageChanged) {
            console.log(`Status changed for site ${siteId} (${oldData.status} → ${newData.status}); refetching list.`);
            fetchSites(true);
            return;
          }

          // If booked_dates changed or any other availability-related field changed
          if (JSON.stringify(newData.booked_dates) !== JSON.stringify(oldData.booked_dates)) {
            console.log('Booked dates changed for site:', siteId);
            
            // Only refresh if we have date filters applied
            if (appliedStartDate && appliedEndDate) {
              // Check if this site is in our current view
              const affectedSiteIndex = sites.findIndex(s => s.id === siteId);
              
              if (affectedSiteIndex !== -1) {
                // Instead of a full refresh, check if this site is now unavailable for our dates
                checkSiteAvailability(siteId, appliedStartDate, appliedEndDate).then(available => {
                  console.log(`Site ${siteId} availability check:`, available);
                  
                  setSites(prevSites => {
                    const updatedSites = [...prevSites];
                    const site = {...updatedSites[affectedSiteIndex]};
                    
                    // Update availability status based on check
                    if (site.available !== !available) {
                      site.available = !available;
                      
                      // If the site became unavailable, show a toast
                      if (!available) {
                        toast({
                          title: "Site Unavailable",
                          description: `Site ${site.siteNumber} is now unavailable for your selected dates.`,
                          variant: "destructive"
                        });
                      }
                    }
                    
                    // Update booked dates
                    if (newData.booked_dates) {
                      site.bookedDates = formatBookedDates(newData.booked_dates);
                    }
                    
                    updatedSites[affectedSiteIndex] = site;
                    return updatedSites;
                  });
                }).catch(err => {
                  console.error('Error checking site availability:', err);
                });
              }
            } else {
              // No date filters, just refresh the whole list
              fetchSites(true);
            }
          }
        }
      })
      .subscribe((status) => {
        console.log(`Sites subscription status: ${status}`);
      });
    
    console.log('Subscribed to camping site changes');
    
    // Cleanup subscription on component unmount
    return () => {
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(sitesChannel);
    };
  }, [sites, toast, appliedStartDate, appliedEndDate]);

  // Helper function to check site availability directly
  const checkSiteAvailability = async (siteId: string, startDate: Date, endDate: Date): Promise<boolean> => {
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      // Use the database function to check availability
      const { data: isAvailable, error } = await supabase
        .rpc('check_site_availability_for_dates', {
          site_id: siteId,
          check_start_date: formattedStartDate,
          check_end_date: formattedEndDate
        });
      
      if (error) {
        console.error('Error checking site availability:', error);
        return false;
      }
      
      return !!isAvailable;
    } catch (err) {
      console.error('Error in checkSiteAvailability:', err);
      return false;
    }
  };
  
  // Helper function to format booked dates from database format
  const formatBookedDates = (bookedDatesArray: any[]): {start: string, end: string}[] => {
    if (!bookedDatesArray || !Array.isArray(bookedDatesArray)) return [];
    
    const formattedDates: {start: string, end: string}[] = [];
    
    bookedDatesArray.forEach(dateRange => {
      try {
        if (typeof dateRange === 'string') {
          // Try to parse from string format like '["2025-05-01 00:00:00","2025-05-03 00:00:00")'
          const cleanedStr = dateRange.replace(/[\[\]\(\)"]/g, '');
          const parts = cleanedStr.split(',');
          
          if (parts.length === 2) {
            const start = parts[0].trim().split(' ')[0]; // Get just the date part
            const end = parts[1].trim().split(' ')[0];   // Get just the date part
            formattedDates.push({ start, end });
          }
        } else if (dateRange && typeof dateRange === 'object') {
          // Try to handle object format if available
          const rangeStr = String(dateRange);
          if (rangeStr.includes(',')) {
            const parts = rangeStr.replace(/[\[\]\(\)"]/g, '').split(',');
            if (parts.length === 2) {
              const start = parts[0].trim().split(' ')[0]; // Get just the date part
              const end = parts[1].trim().split(' ')[0];   // Get just the date part
              formattedDates.push({ start, end });
            }
          }
        }
      } catch (err) {
        console.error('Error formatting date range:', err, dateRange);
      }
    });
    
    return formattedDates;
  };
  
  // Handler for new booking creation
  const handleBookingCreated = (bookingData: Record<string, any>) => {
    // Only handle if the booking is paid - pending bookings don't affect availability
    if (bookingData.payment_status !== 'paid') {
      console.log('Ignoring non-paid booking creation');
      return;
    }
    
    console.log('Handling new paid booking:', bookingData);
    
    setSites(prevSites => {
      // Make a copy of the sites array
      const updatedSites = [...prevSites];
      const affectedSiteIndex = updatedSites.findIndex(s => s.id === bookingData.site_id);
      
      if (affectedSiteIndex !== -1) {
        // Get the affected site
        const updatedSite = {...updatedSites[affectedSiteIndex]};
        const bookingStart = new Date(bookingData.start_date);
        const bookingEnd = new Date(bookingData.end_date);
        
        // Check if booking overlaps with currently selected date range
        const isOverlapping = appliedStartDate && appliedEndDate && (
          (bookingStart <= appliedEndDate && bookingEnd >= appliedStartDate)
        );
        
        // If the booking overlaps with our selected dates, mark site as unavailable
        if (isOverlapping) {
          updatedSite.available = false;
          
          toast({
            title: "Site Just Booked",
            description: `Site ${updatedSite.siteNumber} has just been booked for your selected dates.`,
            variant: "destructive"
          });
        }
        
        // Update the booked dates array if it exists
        if (updatedSite.bookedDates) {
          updatedSite.bookedDates = [
            ...updatedSite.bookedDates,
            { start: bookingData.start_date, end: bookingData.end_date }
          ];
        } else {
          updatedSite.bookedDates = [
            { start: bookingData.start_date, end: bookingData.end_date }
          ];
        }
        
        updatedSites[affectedSiteIndex] = updatedSite;
      }
      
      return updatedSites;
    });
    
    // Additionally, trigger a refresh for this site through our context
    if (bookingData.site_id && bookingData.payment_status === 'paid') {
      refreshAvailability(bookingData.site_id);
    }
  };
  
  // Handler for booking updates
  const handleBookingUpdated = (bookingData: Record<string, any>) => {
    // Only care about status changes to "paid" since that's when a site actually becomes unavailable
    if (bookingData.payment_status === 'paid') {
      console.log('Processing booking update to paid status');
      setSites(prevSites => {
        // Make a copy of the sites array
        const updatedSites = [...prevSites];
        const affectedSiteIndex = updatedSites.findIndex(s => s.id === bookingData.site_id);
        
        if (affectedSiteIndex !== -1) {
          const updatedSite = {...updatedSites[affectedSiteIndex]};
          const bookingStart = new Date(bookingData.start_date);
          const bookingEnd = new Date(bookingData.end_date);
          
          // Check if booking overlaps with currently selected date range
          const isOverlapping = appliedStartDate && appliedEndDate && (
            (bookingStart <= appliedEndDate && bookingEnd >= appliedStartDate)
          );
          
          // If the booking overlaps with our selected dates, mark site as unavailable
          if (isOverlapping) {
            updatedSite.available = false;
            
            toast({
              title: "Booking Confirmed",
              description: `Site ${updatedSite.siteNumber} booking has been confirmed and is no longer available.`,
              variant: "default"
            });
          }
          
          // Update the booked dates array
          if (updatedSite.bookedDates) {
            // Check if this date range already exists
            const exists = updatedSite.bookedDates.some(
              date => date.start === bookingData.start_date && date.end === bookingData.end_date
            );
            
            if (!exists) {
              updatedSite.bookedDates = [
                ...updatedSite.bookedDates,
                { start: bookingData.start_date, end: bookingData.end_date }
              ];
            }
          } else {
            updatedSite.bookedDates = [
              { start: bookingData.start_date, end: bookingData.end_date }
            ];
          }
          
          updatedSites[affectedSiteIndex] = updatedSite;
        }
        
        return updatedSites;
      });
      
      // Additionally, trigger a refresh for this site through our context
      if (bookingData.site_id && bookingData.payment_status === 'paid') {
        refreshAvailability(bookingData.site_id);
      }
    }
  };
  
  // Handler for booking deletions (cancellations)
  const handleBookingDeleted = (bookingData: Record<string, any>) => {
    console.log('Booking was canceled or deleted, refreshing availability data');
    // Refresh data to get current availability instead of trying to update local state
    // This ensures we get the correct state from the database
    fetchSites(true);
    
    toast({
      title: "Booking Canceled",
      description: "A booking was just canceled. The site availability has been updated.",
      variant: "default"
    });
    
    // Additionally, trigger a refresh for this site through our context
    if (bookingData.site_id) {
      refreshAvailability(bookingData.site_id);
    }
  };
  
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchSites(true);
  };

  const handleApplyDateFilter = () => {
    setAppliedStartDate(searchStartDate);
    setAppliedEndDate(searchEndDate);
  };
  
  const handleClearDateFilter = () => {
    setSearchStartDate(undefined);
    setSearchEndDate(undefined);
    setAppliedStartDate(undefined);
    setAppliedEndDate(undefined);
  };
  
  const toggleAvailabilityFilter = () => {
    setShowOnlyAvailable(!showOnlyAvailable);
  };
  
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, index) => (
          <Skeleton 
            key={index} 
            className="h-[400px] w-full rounded-lg"
          />
        ))}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-4 py-2 bg-tribal-primary text-white rounded-md hover:bg-tribal-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }
  
  // Add Date Range Filter UI at the top
  const renderDateFilterSection = () => (
    <Card className="p-3 bg-white mb-4">
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <Button 
          variant={showDateFilter ? "default" : "outline"} 
          className="w-full sm:w-auto flex items-center gap-2"
          onClick={() => setShowDateFilter(!showDateFilter)}
        >
          <CalendarRange className="h-4 w-4" />
          {showDateFilter ? "Hide Date Filter" : "Filter by Date"}
        </Button>
        
        <Button
          variant="outline"
          className="w-full sm:w-auto flex items-center gap-2"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCcw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          Refresh Availability
        </Button>
        
        {appliedStartDate && appliedEndDate && (
          <div className="flex items-center gap-2 text-xs md:text-sm px-3 py-1 bg-tribal-primary/10 rounded-full">
            <span className="font-medium">
              {appliedStartDate.toLocaleDateString()} - {appliedEndDate.toLocaleDateString()}
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-5 w-5 rounded-full"
              onClick={handleClearDateFilter}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
        
        {appliedStartDate && appliedEndDate && (
          <div className="flex items-center gap-2">
            <Button
              variant={showOnlyAvailable ? "default" : "outline"}
              size="sm"
              onClick={toggleAvailabilityFilter}
              className="ml-auto"
            >
              {showOnlyAvailable ? "Showing Available Only" : "Show All Sites"}
            </Button>
            <div className="ml-2 text-sm text-tribal-neutral">
              <span className="font-medium text-tribal-primary">{sites.length}</span> sites
            </div>
          </div>
        )}
      </div>
      
      {showDateFilter && (
        <div className="mt-3 p-3 border rounded-md">
          <DateRangePicker
            startDate={searchStartDate}
            endDate={searchEndDate}
            setStartDate={setSearchStartDate}
            setEndDate={setSearchEndDate}
            startLabel="Check-in Date"
            endLabel="Check-out Date"
            className="w-full"
            displayUnit="nights"
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={handleClearDateFilter}
            >
              Clear
            </Button>
            <Button
              onClick={handleApplyDateFilter}
              disabled={!searchStartDate || !searchEndDate}
              className="flex items-center gap-2"
            >
              <Search className="h-4 w-4" />
              Search Availability
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
  
  // Display a message if no sites available after filtering
  if (sites.length === 0) {
    return (
      <div>
        {renderDateFilterSection()}
        <div className="p-8 text-center">
          <p className="text-tribal-neutral">No camping sites available for the selected filters.</p>
          {(appliedStartDate && appliedEndDate) && (
            <Button 
              onClick={handleClearDateFilter}
              className="mt-4"
              variant="outline"
            >
              Clear Date Filter
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {renderDateFilterSection()}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sites.map(site => {
          const siteAmenities = site.amenities as FrontendAmenities;
          
          const isRVSite = site.siteType === 'rv' || site.siteNumber.toLowerCase().includes('rv');
          const isSheepCreekRV = site.location === 'sheep-creek' && isRVSite;
          
          if (isSheepCreekRV && site.hasElectricity) {
            (siteAmenities as any).electric50Amp = true;
          }
          
          return (
            <CampSiteCard
              key={site.id}
              site={{
                id: site.id,
                name: site.siteNumber,
                location: site.location === 'mountain-view' ? 'Mountain View' : 
                        site.location === 'sheep-creek' ? 'Sheep Creek' : 'Wild Horse',
                price: site.pricePerNight,
                image: `/lovable-uploads/3bdf8f99-6c1c-425a-9c15-6cc49b6cc262.png`,
                capacity: site.maxOccupants,
                amenities: siteAmenities,
                available: site.available !== false, // Use explicit check for false so undefined is treated as available
                status: site.status ?? 'active',
                statusMessage: site.statusMessage ?? null
              }}
              onBook={(siteId) => {
                toast({
                  title: "Booking initiated",
                  description: `You're booking ${site.siteNumber}. Select dates on the next page.`,
                });
                
                console.log(`Initiating booking process for site ${siteId}`);
                
                // Navigate to the booking page with proper URL parameters
                navigate(`/booking/${siteType}/${siteId}`);
              }}
              startDate={appliedStartDate}
              endDate={appliedEndDate}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CampSiteGrid;
